import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgeCheck, Calendar, Download, MapPin } from "lucide-react";
import { useApi, mediaUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { track } from "../lib/store";
import { fmtGregorian, fmtHijri } from "../lib/format";
import { DemoBadge, Loading, Reveal } from "../components/Common";
import { Lightbox } from "../components/Gallery";
import ProgressTimeline from "../components/ProgressTimeline";
import PropertyCard from "../components/PropertyCard";
import LeadForm from "../components/LeadForm";
import { PropertiesMap } from "../components/Maps";
import Seo from "../components/Seo";
import { Unavailable } from "./NotFound";
import { ProjectLocation, FloorPlanGroups, UnitsTable, brochureHref } from "../components/ProjectSections";

const Fact = ({ icon: Icon, label, children, testid }) => (
  <div className="border-s border-cta/30 ps-5">
    <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-fg/45"><Icon className="h-3.5 w-3.5 text-cta" />{label}</p>
    <div data-testid={testid} className="mt-2 text-fg">{children}</div>
  </div>
);

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t, tr, lang } = useI18n();
  const { label } = useSite();
  const { data, loading, error } = useApi(`/projects/${slug}`);
  const [unitMsg, setUnitMsg] = useState("");
  const [unitId, setUnitId] = useState(null);
  useEffect(() => {
    if (data && window.location.hash) setTimeout(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400);
  }, [data]);
  const [open, setOpen] = useState(null);
  const p = data?.project;
  useEffect(() => { if (p) track("project_view", { project_id: p.id }); }, [p]);
  if (loading) return <Loading />;
  if (error || !p) return <Unavailable kind="project" />;
  const gallery = [p.master_plan_image, ...p.gallery].filter(Boolean);
  return (
    <article data-testid="project-detail-page">
      <Seo seo={p.seo} title={tr(p, "title")} description={tr(p, "summary")} image={mediaUrl(p.cover_image)} />
      <section className="on-media relative flex min-h-[78vh] items-end overflow-hidden pb-16 pt-36">
        {p.cover_image && <img src={mediaUrl(p.cover_image)} alt={tr(p, "title")} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/60 to-canvas/30" />
        <div className="mashrabiya absolute inset-0 opacity-[0.05]" aria-hidden="true" />
        <div className="container-lux relative">
          <Reveal>
            <div className="mb-5 flex flex-wrap gap-2">
              {p.is_demo && <DemoBadge label={t("project.demo")} testid="project-demo-badge" />}
              <span className="eyebrow">{label("project_category", p.category)}</span>
            </div>
            <h1 data-testid="project-title" className="h-display text-5xl sm:text-6xl lg:text-7xl">{tr(p, "title")}</h1>
            <p className="mt-5 max-w-2xl text-lg text-fg/75">{tr(p, "summary")}</p>
          </Reveal>
        </div>
      </section>

      <div className="container-lux grid gap-8 border-b border-fg/10 py-10 sm:grid-cols-3">
        <Fact icon={BadgeCheck} label={t("project.status")} testid="project-status">{tr(p, "status")}</Fact>
        <Fact icon={MapPin} label={t("project.location")} testid="project-location">{tr(p, "location")}</Fact>
        <Fact icon={Calendar} label={t("project.est")} testid="project-est-completion">
          {p.est_completion ? <>{fmtGregorian(p.est_completion, lang)}<span className="block text-xs text-fg/50">{fmtHijri(p.est_completion, lang)}</span></> : t("project.tba")}
        </Fact>
      </div>

      <div className="container-lux grid grid-cols-1 gap-14 py-16 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-16">
          <Reveal><p className="text-lg leading-relaxed text-fg/75">{tr(p, "description")}</p></Reveal>
          {p.amenities.length > 0 && (
            <section><h2 className="h-display mb-6 text-3xl">{t("property.amenities")}</h2>
              <ul className="flex flex-wrap gap-2">{p.amenities.map((a) => (<li key={a} className="chip">{label("amenity", a)}</li>))}</ul></section>
          )}
          {gallery.length > 0 && (
            <section><h2 className="h-display mb-6 text-3xl">{p.master_plan_image ? `${t("project.masterPlan")} & ${t("project.gallery")}` : t("project.gallery")}</h2>
              <div data-testid="project-gallery" className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {gallery.map((g, i) => (<button key={g + i} type="button" data-testid={`project-gallery-image-${i}`} onClick={() => setOpen(i)} className={`overflow-hidden rounded-sm border border-fg/10 ${i === 0 ? "col-span-2 row-span-2" : ""}`}><img src={mediaUrl(g)} alt={`${tr(p, "title")} ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" /></button>))}
              </div>
              <Lightbox images={gallery} index={open} onClose={() => setOpen(null)} title={tr(p, "title")} />
            </section>
          )}
          <ProjectLocation p={p} />
          <FloorPlanGroups p={p} />
          <section id="progress" className="scroll-mt-28"><h2 data-testid="project-progress-heading" className="h-display mb-10 text-3xl">{t("project.progress")}</h2><ProgressTimeline updates={p.progress_updates} /></section>
          <section id="units" className="scroll-mt-28"><h2 className="h-display mb-6 text-3xl">{t("project.units")}</h2>
            {data.units.some((u) => u.tower) && <div className="mb-8"><UnitsTable units={data.units} onInquire={(u) => { setUnitId(u.id); setUnitMsg(`${lang === "ar" ? "أرغب في الاستفسار عن" : "I'd like to inquire about"} ${tr(u, "title")} (Ref: ${u.ref})`); document.getElementById("project-inquiry")?.scrollIntoView({ behavior: "smooth", block: "center" }); }} /></div>}
            {data.units.length ? <div data-testid="project-units" className="grid gap-6 sm:grid-cols-2">{data.units.map((u) => (<PropertyCard key={u.id} p={u} />))}</div>
              : <p data-testid="project-no-units" className="text-fg/55">{t("project.noUnits")}</p>}
          </section>
          {p.lat && <PropertiesMap items={[{ ...p, slug: p.slug }]} single className="h-[340px]" />}
        </div>
        <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
          <a href={brochureHref(p, lang)} target="_blank" rel="noopener noreferrer" data-testid="project-brochure-button" className="btn-ghost w-full"><Download className="h-4 w-4" />{t("project.brochure")}</a>
          <div className="glass rounded-sm p-6" id="project-inquiry">
            <h2 className="h-display mb-5 text-xl">{t("project.inquiry")}</h2>
            <LeadForm key={unitMsg} type="inquiry" projectId={p.id} propertyId={unitId || undefined} prefix="project-inquiry" defaultMessage={unitMsg || `${t("property.waMsg")} ${tr(p, "title")}`} />
          </div>
        </aside>
      </div>
    </article>
  );
}
